import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import { NotificationsNone } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../context/NotificationContext";
import { NOTIFICATION_TYPES } from "../../notifications/types";
import { summarizeStoredOverdueNotifications } from "../../notifications/taskOverdueDigest";
import {
  dashboardNotificationSubtitle,
  splitDashboardNotificationSubtitle,
  withDashboardNavigationNonce,
} from "../../utils/dashboardDeepLink";

const lineClampSx = (lines) => ({
  overflow: "hidden",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: lines,
  whiteSpace: "normal",
  wordBreak: "break-word",
});

export default function NotificationBell() {
  const { t, i18n } = useTranslation("notifications");
  const navigate = useNavigate();
  const { inbox, unreadCount, markRead, markAllRead, prefsSyncFailed } = useNotifications();
  const [anchor, setAnchor] = useState(null);

  const open = Boolean(anchor);

  const openPanel = (e) => setAnchor(e.currentTarget);
  const closePanel = () => setAnchor(null);

  const groups = useMemo(() => {
    const map = new Map(); // key -> { key, conversationId, items[], latest, unreadCount, totalCount, meta, deepLink }
    for (const it of inbox) {
      const isTeam =
        it.type === NOTIFICATION_TYPES.team_message ||
        it.type === NOTIFICATION_TYPES.team_mention;
      const isOverdueTask = it.type === NOTIFICATION_TYPES.task_overdue;
      const key = isOverdueTask
        ? "task-overdue"
        : isTeam && it.spaceId
        ? `team-${it.spaceId}`
        : isTeam && it.deepLink
          ? it.deepLink
          : it.conversationId || it.deepLink || it.id;
      const g = map.get(key) || {
        key,
        conversationId: it.conversationId || null,
        items: [],
        latest: null,
        unreadCount: 0,
        totalCount: 0,
        requestMeta: null,
        deepLink: null,
        openLinkInNewTab: false,
      };
      g.items.push(it);
      if (it.openLinkInNewTab) g.openLinkInNewTab = true;
      const groupedTotal = Number(it.teamGroupedCount) || 1;
      const groupedUnread = Number(it.teamGroupedUnread);
      g.totalCount += groupedTotal;
      if (!it.read) {
        g.unreadCount += Number.isFinite(groupedUnread) ? groupedUnread : 1;
      }
      if (!g.latest || new Date(it.createdAt) > new Date(g.latest.createdAt)) g.latest = it;
      if (!g.deepLink && it.deepLink) g.deepLink = it.deepLink;
      if (!g.requestMeta && it.requestMeta) g.requestMeta = it.requestMeta;
      map.set(key, g);
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.latest?.createdAt || 0) - new Date(a.latest?.createdAt || 0)
    );
  }, [inbox]);

  const fmtRequestSummary = (meta) => {
    if (!meta) return "";
    const chain = [meta.typeName, meta.subtype1, meta.subtype2, meta.subtype3]
      .map((s) => String(s || "").trim())
      .filter(Boolean);
    const typePart = chain.length ? chain.join(" > ") : "";
    const originPart = meta.originName ? String(meta.originName).trim() : "";
    return [typePart, originPart].filter(Boolean).join(" • ");
  };

  const handleOpenGroup = (group) => {
    const idsToMark = group.items.filter((x) => !x.read).map((x) => x.id);
    if (idsToMark.length) markRead(idsToMark);

    const isDashboardReport =
      group.latest?.type === NOTIFICATION_TYPES.dashboard_report_ready;

    if (group.deepLink) {
      const targetLink = isDashboardReport
        ? withDashboardNavigationNonce(group.deepLink)
        : group.deepLink;

      if (group.openLinkInNewTab) {
        let url;
        try {
          url = new URL(targetLink, window.location.origin).href;
        } catch {
          url = targetLink;
        }
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        navigate(targetLink);
      }
    }
    closePanel();
  };

  return (
    <>
      <Tooltip title={prefsSyncFailed ? t("bell.prefsOutOfSync") : t("bell.tooltip")}>
        <IconButton
          onClick={openPanel}
          aria-label={prefsSyncFailed ? t("bell.prefsOutOfSync") : t("bell.tooltip")}
          sx={{ marginLeft: 1, color: prefsSyncFailed ? "warning.main" : "text.primary" }}
          className="tour-notifications-bell"
        >
          <Badge color="error" badgeContent={unreadCount > 99 ? "99+" : unreadCount} invisible={unreadCount === 0}>
            <NotificationsNone />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closePanel}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 360, maxWidth: "calc(100vw - 24px)", maxHeight: 420 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t("titles.inbox")}
          </Typography>
          {unreadCount > 0 ? (
            <Button size="small" onClick={() => markAllRead()}>
              {t("bell.markAll")}
            </Button>
          ) : null}
        </Box>
        <Divider />
        {groups.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary" variant="body2">
              {t("bell.empty")}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ py: 0.5 }}>
            {groups.slice(0, 25).map((group) => {
              const meta = group.requestMeta;
              const protocol = meta?.protocol || group.conversationId || null;
              const clientName = meta?.clientName ? String(meta.clientName).trim() : "";
              const isLegacy = group.latest?.type === NOTIFICATION_TYPES.legacy_socket;
              const isDashboardReport =
                group.latest?.type === NOTIFICATION_TYPES.dashboard_report_ready;
              const isTeam =
                group.latest?.type === NOTIFICATION_TYPES.team_message ||
                group.latest?.type === NOTIFICATION_TYPES.team_mention;
              const overdueSummary =
                group.key === "task-overdue"
                  ? summarizeStoredOverdueNotifications(group.items, t)
                  : null;
              const primary = overdueSummary
                ? overdueSummary.title
                : isDashboardReport
                ? group.latest?.title || t("titles.dashboardReportReady", "Relatório do dashboard")
                : protocol
                ? `Solicitação #${protocol}${clientName ? ` — ${clientName}` : ""}`
                : isLegacy
                  ? `${t("titles.legacy")}${t("titles.legacyXpro2Suffix")}`
                  : isTeam
                    ? group.latest?.title || t("titles.teamMessage")
                    : group.latest?.title || t("titles.inbox");

              const summary = fmtRequestSummary(meta);
              const lead =
                isDashboardReport
                  ? dashboardNotificationSubtitle(group.latest, i18n.language)
                  : isLegacy && group.latest?.body
                  ? String(group.latest.body)
                  : isTeam && group.latest?.body
                    ? String(group.latest.body)
                    : !isTeam && group.latest?.title
                      ? String(group.latest.title)
                      : "";
              const dashboardSubtitle = isDashboardReport
                ? splitDashboardNotificationSubtitle(lead, t)
                : null;
              const cacheExpired = Boolean(dashboardSubtitle?.cacheExpired);
              const extraCount =
                overdueSummary || group.totalCount <= 1 ? "" : `+${group.totalCount - 1}`;
              const secondary = overdueSummary
                ? overdueSummary.body
                : isDashboardReport
                ? dashboardSubtitle?.text || lead
                : [lead, summary].filter(Boolean).join(" • ");

              return (
              <ListItemButton
                key={group.key}
                onClick={() =>
                  handleOpenGroup(
                    overdueSummary?.deepLink
                      ? { ...group, deepLink: overdueSummary.deepLink }
                      : group
                  )
                }
                sx={{
                  alignItems: "flex-start",
                  bgcolor: group.unreadCount > 0 ? "action.hover" : "transparent",
                  borderLeft: 3,
                  borderColor:
                    group.latest?.type === "new_request" ||
                    group.latest?.type === "voice_queue" ||
                    group.latest?.type === "request_assigned"
                      ? "warning.main"
                      : group.latest?.type === NOTIFICATION_TYPES.team_mention
                        ? "warning.main"
                      : group.latest?.type === "security" ||
                        group.latest?.type === NOTIFICATION_TYPES.task_overdue
                        ? "error.main"
                        : isDashboardReport && cacheExpired
                        ? "warning.main"
                        : isDashboardReport
                        ? "info.main"
                      : isTeam
                          ? "secondary.main"
                          : "primary.main",
                }}
              >
                <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ListItemText
                      primary={primary}
                      secondary={secondary}
                      primaryTypographyProps={{
                        fontWeight: group.unreadCount > 0 ? 700 : 500,
                        variant: "body2",
                        sx: lineClampSx(2),
                      }}
                      secondaryTypographyProps={{
                        variant: "caption",
                        color: cacheExpired ? "warning.main" : "text.secondary",
                        sx: lineClampSx(3),
                      }}
                    />
                  </Box>
                  {cacheExpired ? (
                    <Chip
                      size="small"
                      color="warning"
                      label={t("bell.dashboardCacheExpiredShort", { defaultValue: "Expirado" })}
                      sx={{ alignSelf: "center", fontWeight: 700 }}
                    />
                  ) : null}
                  {group.unreadCount > 0 && !overdueSummary ? (
                    <Chip
                      size="small"
                      color="error"
                      label={group.unreadCount > 99 ? "99+" : String(group.unreadCount)}
                      sx={{ alignSelf: "center", fontWeight: 800 }}
                    />
                  ) : null}
                  {extraCount ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={extraCount}
                      sx={{ alignSelf: "center", fontWeight: 700 }}
                    />
                  ) : null}
                </Box>
              </ListItemButton>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
